import { useMemo } from 'react';
import { Card } from '../ui/Card';
import { fmtDollar, fmtDollarInt } from '../../lib/format';
import { computeAffordabilityThresholds, computeAtDelta } from '../../lib/price-levels';
import type { AffordabilityThresholds } from '../../lib/price-levels';
import type { CalculatorInputs } from '../../lib/types';

// ── Color mapping ──────────────────────────────────────────────────

type IndicatorColor = 'green' | 'yellow' | 'red';

const COLOR_FILLS: Record<IndicatorColor, string> = {
  green: 'rgba(22,163,74,0.18)',
  yellow: 'rgba(217,119,6,0.18)',
  red: 'rgba(220,38,38,0.18)',
};

/** Convert a pctDelta (-0.05 … +0.05) to a % position on the bar (0 … 100). */
function deltaToBarPct(delta: number): number {
  return ((delta + 0.05) / 0.10) * 100;
}

/** Build a CSS linear-gradient string from the color segments. */
function buildGradient(metric: AffordabilityThresholds['payment']): string {
  const stops: string[] = [];
  let pos = 0;

  const segments: { color: IndicatorColor; endPct: number }[] = [];

  if (metric.greenYellowDelta !== null) {
    const pct = deltaToBarPct(metric.greenYellowDelta);
    segments.push({ color: 'green', endPct: pct });
    if (metric.yellowRedDelta !== null) {
      segments.push({ color: 'yellow', endPct: deltaToBarPct(metric.yellowRedDelta) });
      segments.push({ color: 'red', endPct: 100 });
    } else {
      segments.push({ color: metric.colorAtMax === 'red' ? 'red' : 'yellow', endPct: 100 });
    }
  } else if (metric.yellowRedDelta !== null) {
    const pct = deltaToBarPct(metric.yellowRedDelta);
    segments.push({ color: 'yellow', endPct: pct });
    segments.push({ color: 'red', endPct: 100 });
  } else {
    segments.push({ color: metric.colorAtMin, endPct: 100 });
  }

  for (const seg of segments) {
    const fill = COLOR_FILLS[seg.color];
    stops.push(`${fill} ${pos}%`, `${fill} ${seg.endPct}%`);
    pos = seg.endPct;
  }

  return `linear-gradient(to right, ${stops.join(', ')})`;
}

// ── Unified breakpoint ─────────────────────────────────────────────

interface Breakpoint {
  delta: number;
  barPct: number;
  deltaLabel: string;
  price: string;
  monthly: string;
  cash: string;
  cashGap: number; // positive = shortfall, negative = surplus
}

/**
 * Collect all unique threshold deltas from both metrics, deduplicate
 * nearby values (within 0.3%), and compute price/monthly/cash at each.
 */
function collectBreakpoints(
  thresholds: AffordabilityThresholds,
  askingPrice: number,
  baseInputs: CalculatorInputs,
  cashOnHand: number,
): Breakpoint[] {
  const deltas: number[] = [];
  for (const metric of [thresholds.payment, thresholds.cash]) {
    if (metric.greenYellowDelta !== null) deltas.push(metric.greenYellowDelta);
    if (metric.yellowRedDelta !== null) deltas.push(metric.yellowRedDelta);
  }

  // Deduplicate: sort and merge deltas within 0.3% of each other
  deltas.sort((a, b) => a - b);
  const unique: number[] = [];
  for (const d of deltas) {
    if (unique.length === 0 || d - unique[unique.length - 1] > 0.003) {
      unique.push(d);
    } else {
      unique[unique.length - 1] = (unique[unique.length - 1] + d) / 2;
    }
  }

  return unique.map(delta => {
    const v = computeAtDelta(askingPrice, baseInputs, delta);
    const offerPrice = Math.round(askingPrice * (1 + delta));
    const sign = delta >= 0 ? '+' : '';
    return {
      delta,
      barPct: deltaToBarPct(delta),
      deltaLabel: `${sign}${(delta * 100).toFixed(1)}%`,
      price: fmtDollarInt(offerPrice),
      monthly: fmtDollar(v.monthlyPayment),
      cash: fmtDollarInt(v.cashToClose),
      cashGap: v.cashToClose - cashOnHand,
    };
  });
}

// ── Main card ──────────────────────────────────────────────────────

interface Props {
  askingPrice: number;
  baseInputs: CalculatorInputs;
  cashOnHand: number;
  grossMonthlyIncome: number;
  onOpenCalculator: () => void;
  onPlanOffer: () => void;
}

export function AffordabilityBarsCard({
  askingPrice,
  baseInputs,
  cashOnHand,
  grossMonthlyIncome,
  onOpenCalculator,
  onPlanOffer,
}: Props) {
  const thresholds = useMemo(
    () => computeAffordabilityThresholds(askingPrice, baseInputs, cashOnHand, grossMonthlyIncome),
    [askingPrice, baseInputs, cashOnHand, grossMonthlyIncome]
  );

  const breakpoints = useMemo(
    () => collectBreakpoints(thresholds, askingPrice, baseInputs, cashOnHand),
    [thresholds, askingPrice, baseInputs, cashOnHand]
  );

  const paymentGradient = useMemo(() => buildGradient(thresholds.payment), [thresholds.payment]);
  const cashGradient = useMemo(() => buildGradient(thresholds.cash), [thresholds.cash]);

  return (
    <Card title="Affordability Overview">
      {/* Bar labels sit outside the positioned area */}
      <div className="ab-bar-header">
        <span className="ab-bar-title">Monthly Payment</span>
        <span className="ab-bar-value">{fmtDollar(thresholds.atAsking.monthlyPayment)}/mo</span>
      </div>

      {/* Positioned container: only the two tracks + overlay lines live here */}
      <div className="ab-bars">
        <div className="ab-track">
          <div className="ab-fill" style={{ background: paymentGradient }} />
        </div>

        <div className="ab-bar-label-row">
          <span className="ab-bar-title">Cash to Close</span>
          <span className="ab-bar-value">{fmtDollarInt(thresholds.atAsking.cashToClose)}</span>
        </div>

        <div className="ab-track">
          <div className="ab-fill" style={{ background: cashGradient }} />
        </div>

        {/* Asking-price center line — spans only the tracks */}
        <div className="ab-asking-line" />

        {/* Breakpoint lines — span only the tracks */}
        {breakpoints.map((bp, i) => (
          <div key={i} className="ab-break-line" style={{ left: `${bp.barPct}%` }}>
            <span className="ab-break-label">{bp.deltaLabel}</span>
          </div>
        ))}
      </div>

      {/* Range labels: -5% / Asking / +5% */}
      <div className="ab-range-labels">
        <span>−5%</span>
        <span className="ab-range-asking">Asking</span>
        <span>+5%</span>
      </div>

      {/* Breakpoint detail table */}
      <h4 className="ab-table-heading">Key Price Points</h4>
      <table className="ab-break-table">
        <thead>
          <tr>
            <th>Offer</th>
            <th>Price</th>
            <th>Monthly</th>
            <th>Cash to Close</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const askingCashGap = thresholds.atAsking.cashToClose - cashOnHand;
            const askingIsShort = askingCashGap > 0;

            // Build rows: breakpoints + asking, sorted by delta
            type Row = { delta: number; deltaLabel: string; price: string; monthly: string; cash: string; cashGap: number; isAsking: boolean };
            const rows: Row[] = [
              ...breakpoints.map(bp => ({ ...bp, isAsking: false })),
              {
                delta: 0,
                deltaLabel: 'Asking',
                price: fmtDollarInt(askingPrice),
                monthly: fmtDollar(thresholds.atAsking.monthlyPayment),
                cash: fmtDollarInt(thresholds.atAsking.cashToClose),
                cashGap: askingCashGap,
                isAsking: true,
              },
            ];
            rows.sort((a, b) => a.delta - b.delta);

            return rows.map((row, i) => {
              const isShort = row.cashGap > 0;
              return (
                <tr key={i} className={row.isAsking ? 'ab-asking-row' : ''}>
                  <td className={row.isAsking ? 'ab-break-table-asking' : 'ab-break-table-delta'}>{row.deltaLabel}</td>
                  <td>{row.price}</td>
                  <td>{row.monthly}</td>
                  <td>
                    {row.cash}
                    {cashOnHand > 0 && (
                      <span className={`ab-break-table-gap ${isShort ? 'ab-gap-short' : 'ab-gap-surplus'}`}>
                        {isShort ? '−' : '+'}{fmtDollarInt(Math.abs(row.cashGap))}
                      </span>
                    )}
                  </td>
                </tr>
              );
            });
          })()}
        </tbody>
      </table>

      <div className="affordability-actions">
        <button className="btn btn-secondary" onClick={onOpenCalculator}>
          Open Full Calculator
        </button>
        <button className="btn btn-primary" onClick={onPlanOffer}>
          Plan Offer at Asking
        </button>
      </div>

      <div className="affordability-legend">
        <span><span className="indicator indicator-green" /> Within budget</span>
        <span><span className="indicator indicator-yellow" /> Tight</span>
        <span><span className="indicator indicator-red" /> Over budget</span>
      </div>
    </Card>
  );
}
